/**
 * useConcierge Hook
 * Fetches the customer's assigned concierge/agent for personalized service
 */

import { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { AssignedAgent, ActiveSearch, ConciergeData } from '@/types/customer';

// ============================================================================
// Types
// ============================================================================

interface UseConciergeReturn {
  /** The assigned agent/concierge */
  agent: AssignedAgent | null;
  /** Active property searches */
  activeSearches: ActiveSearch[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh the concierge data */
  refresh: () => Promise<void>;
}

// ============================================================================
// Hook
// ============================================================================

export function useConcierge(): UseConciergeReturn {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [agent, setAgent] = useState<AssignedAgent | null>(null);
  const [activeSearches, setActiveSearches] = useState<ActiveSearch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch concierge data
  const refresh = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;

    setIsLoading(true);
    setError(null);

    try {
      // Try to fetch from the dedicated concierge endpoint
      const conciergeData: ConciergeData = await client.getConcierge();
      setAgent(conciergeData.agent);
      setActiveSearches(conciergeData.activeSearches || []);
    } catch (err) {
      // If the concierge endpoint doesn't exist yet (404), 
      // we fall back gracefully without setting an error
      const errorMessage = err instanceof Error ? err.message : 'Failed to load concierge';
      
      // Only set error for non-404 errors (404 means API not implemented yet)
      if (!errorMessage.includes('404') && !errorMessage.includes('Not Found')) {
        console.warn('[useConcierge] API not available, using fallback:', errorMessage);
      }
      
      // Set agent to null - the UI should handle this gracefully
      setAgent(null);
      setActiveSearches([]);
    } finally {
      setIsLoading(false);
    }
  }, [getCustomerClient]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setAgent(null);
      setActiveSearches([]);
    }
  }, [isAuthenticated, refresh]);

  return {
    agent,
    activeSearches,
    isLoading,
    error,
    refresh,
  };
}

export default useConcierge;
